// Curated documentation for the VitalEngine reporting DB.
// Structured as: category ordering → table metadata → per-column descriptions.
// For columns not listed explicitly per-table, GENERIC_COLUMNS + naming
// heuristics fill in a reasonable default.

export const CATEGORIES = [
  {
    id: 'referrals',
    title: 'Referrals',
    description:
      'Core workflow: a provider creates a referral, which routes through facilities, users, and groups. Rows track the referral itself plus its timeline, messages, participants, and attachments.',
    tables: [
      'rpt_referral',
      'rpt_all_referrals',
      'rpt_vendor_referrals',
      'rpt_referral_events',
      'rpt_referral_messages',
      'rpt_referral_participants',
      'rpt_referral_tags',
      'rpt_referral_images',
      'rpt_referral_whiteboard',
      'rpt_referral_with_groups',
    ],
  },
  {
    id: 'messaging',
    title: 'Chats & VMail',
    description:
      'Direct chats, group chats, and VMail threads. The chat model is real-time peer messaging; VMail is asynchronous clinical messaging that can carry DICOM and documents.',
    tables: [
      'rpt_chats',
      'rpt_chat_message',
      'rpt_chat_participants',
      'rpt_vmails',
      'rpt_vmail_messages',
      'rpt_vmail_participants',
      'rpt_vmail_images',
      'rpt_vmail_medical_records',
    ],
  },
  {
    id: 'vrounds',
    title: 'VRounds',
    description:
      'Multi-disciplinary case review sessions. A VRound contains cases, cases link to referrals, and participants are recorded for attendance and access control.',
    tables: [
      'rpt_vrounds',
      'rpt_vrounds_cases',
      'rpt_vrounds_cases_referrals',
      'rpt_vrounds_participants',
    ],
  },
  {
    id: 'studies',
    title: 'Studies & DICOM',
    description:
      'DICOM studies that arrive in VitalEngine, either via the desktop uploader, the outgoing file transfer service, or as attachments on referrals / VMails.',
    tables: [
      'rpt_study',
      'rpt_study_images',
      'rpt_study_permissions',
      'rpt_dicom_outgoing_file',
    ],
  },
  {
    id: 'telemedicine',
    title: 'Telemedicine',
    description:
      'Scheduled telemedicine appointments and their participants (providers, patients, and guest invitees).',
    tables: [
      'rpt_telemedicine_appointments',
      'rpt_telemedicine_appointment_participants',
    ],
  },
  {
    id: 'users-facilities',
    title: 'Users & Facilities',
    description:
      'The directory: users, facilities, their relationships, login history, and contact info. Non-VitalEngine physicians (used as targets for referrals) also live here.',
    tables: [
      'rpt_user',
      'rpt_prod_users',
      'rpt_user_facilities',
      'rpt_user_groups',
      'rpt_user_group_members',
      'rpt_user_login_history',
      'rpt_user_phone',
      'rpt_non_vital_physicians',
      'rpt_physician_assistants',
      'rpt_facility',
    ],
  },
  {
    id: 'usage-summaries',
    title: 'Usage Summaries',
    description:
      'Pre-aggregated daily rollups that power dashboards. "Consolidated" views are the stable reporting tables; "temp" tables are the staging inputs produced by nightly jobs.',
    tables: [
      'rpt_VE_usage',
      'rpt_consolidated_group_usage',
      'rpt_consolidated_message_usage',
      'rpt_user_summarized_login_usage',
      'rpt_study_summarized_usage',
      'rpt_temp_group_message_usage',
      'rpt_temp_user_message_usage',
      'rpt_temp_study_summarized_facility_usage',
    ],
  },
  {
    id: 'whiteboards-tasks',
    title: 'Whiteboards & Tasks',
    description:
      'Facility-level worklists ("whiteboards") and user-level follow-ups ("vtasks"). Whiteboards pin referrals for visibility; tasks are personal reminders that can link to referrals.',
    tables: [
      'rpt_whiteboard',
      'rpt_whiteboard_workflow_order',
      'rpt_vtask',
      'rpt_vtask_users',
    ],
  },
  {
    id: 'inbound',
    title: 'Inbound & Faxes',
    description:
      'Inbound content pipelines: fax receipts, generic email/metadata inbounds, and the mappings that join that metadata to the resulting studies.',
    tables: [
      'rpt_fax_data',
      'rpt_fax_data_recipients',
      'rpt_meta_data',
      'rpt_meta_data_study',
    ],
  },
  {
    id: 'admin',
    title: 'Admin & Config',
    description:
      'Operational metadata: vendor facility ID mappings and the report execution config.',
    tables: [
      'rpt_config',
      'rpt_vendor_facility_id',
    ],
  },
];

// Column-name → description. Used as a fallback when a per-table override
// isn't provided. Keep this list to unambiguous names.
export const GENERIC_COLUMNS = {
  id: 'Surrogate primary key for this row.',
  GUID: 'Globally unique identifier (binary(16)). Used by clients for optimistic creates / idempotency.',
  created_by: 'User ID of the user who created the record.',
  created_date: 'UTC timestamp when the record was created.',
  created_at: 'UTC timestamp when the record was created.',
  updated_by: 'User ID of the last user to modify the record.',
  updated_date: 'UTC timestamp when the record was last modified.',
  updated_at: 'UTC timestamp when the record was last modified.',
  update_date: 'UTC timestamp when the record was last modified.',
  update_at: 'UTC timestamp when the record was last modified.',
  update_by: 'User ID of the last user to modify the record.',
  deleted_by: 'User ID of the user who soft-deleted the record.',
  deleted_date: 'UTC timestamp when the record was soft-deleted.',
  delete_at: 'UTC timestamp when the record was soft-deleted.',
  deleted_at: 'UTC timestamp when the record was soft-deleted.',
  is_deleted: 'Soft-delete flag. 1 = deleted and hidden from the UI.',
  is_removed: 'Soft-remove flag for relationship rows (e.g. removing a user from a chat without losing history).',
  is_active: 'Active flag. 1 = visible / participating.',
  status: 'Status code — values are table-specific enums.',
  aws_region: 'AWS region where the associated blob / DICOM file lives.',
  http_path: 'URL (or S3 key) to the binary asset.',
  file_size: 'File size, usually in bytes.',
  file_type: 'MIME type or file-extension tag.',
  file_name: 'Original file name as uploaded.',
  order_no: 'Sort order within a parent collection.',
  folder_id: 'Folder / inbox tab the record is filed under.',
  count: 'Counter field used by the UI (e.g. unread count, hit count).',
  annotation: 'Annotation flag (table-specific meaning).',
  annotation_type: 'Annotation type code.',
  annotation_action: 'Annotation action code.',
  is_updated: 'Flag indicating the row has been edited after first write.',
  is_unread: 'Unread flag for the associated participant.',
  unread_msg_count: 'Count of unread messages for the participant.',
  is_pinned: 'Pinned flag for the associated participant.',
  mark_unread: 'User manually flipped the thread back to unread.',
  call_status: 'Active call status code (for chats with in-progress calls).',
  is_chat_muted: 'Participant has muted notifications for this chat.',
  last_msg: 'Denormalized copy of the most recent message text for list views.',
  last_msg_from: 'User ID of the sender of the most recent message.',
  last_msg_date: 'Timestamp of the most recent message.',
  is_primary: '1 if this is the primary relationship (e.g. primary facility for a user).',
};

// Per-table metadata. description appears under the table title; columns is a
// map of column name → human description. Unlisted columns fall back to
// GENERIC_COLUMNS and then a heuristic ("Flag", "Identifier", etc.).
export const TABLE_DOCS = {
  // ---------- REFERRALS ----------
  rpt_referral: {
    displayName: 'Referrals (normalized)',
    description:
      'One row per referral. This is the core referral record; related data (events, messages, participants, attachments) lives in the sibling tables.',
    columns: {
      referral_id: 'Primary key. Internal integer ID used across the referral tables.',
      GUID: 'Referral GUID (binary(16)). Exposed to clients and used in URLs / idempotency keys.',
      referral_for: 'Free-text reason for referral (e.g. "Cardiology consult", "MRI read").',
      referred_by: 'User ID of the provider who initiated the referral.',
      referred_to: 'User ID of the provider the referral was sent to.',
      created_by: 'User ID that actually typed in the referral (may differ from referred_by for assistants).',
      created_date: 'UTC timestamp when the referral was first submitted.',
      referral_status: 'Workflow status enum. See VE product docs for value mapping (new / in-progress / closed / etc.).',
      is_deleted: 'Soft-delete flag.',
      is_referred_by_non_vital_user: '1 if referred_by points at rpt_non_vital_physicians instead of a VitalEngine user.',
      is_referred_to_non_vital_user: '1 if referred_to points at rpt_non_vital_physicians.',
      last_msg: 'Denormalized most-recent message body, for inbox-list display.',
      last_msg_from: 'User ID of the most recent message sender.',
      last_msg_date: 'Timestamp of the most recent message.',
      referral_number: 'Human-readable referral number shown in the UI.',
      insurance: 'Insurance carrier / plan (free text). PHI.',
      primary_care_physician: 'PCP name (free text). PHI.',
      mrn: "Patient medical record number at the originating facility. PHI.",
      referral_type: 'Enum for referral type (standard / wizard / hospital transfer / etc.).',
      no_of_DICOM: 'Number of DICOM studies attached to the referral.',
      no_of_documents: 'Number of non-DICOM documents attached.',
      non_ve_physician_id: 'If referred_to is non-VE, this is the FK into rpt_non_vital_physicians.',
      patient_name: 'Patient display name (often first + last concatenated). PHI.',
      patient_dob: 'Patient date of birth. PHI.',
      time_line: 'Urgency enum (routine / urgent / stat).',
      patient_id: 'Internal patient record ID.',
      first_name: 'Patient first name. PHI.',
      last_name: 'Patient last name. PHI.',
      middle_name: 'Patient middle name. PHI.',
      patient_gender: 'Patient gender (M / F / O / U).',
      email: 'Patient email. PHI.',
      phone_number: 'Patient phone. PHI.',
      address_1: 'Patient street address. PHI.',
      city: 'Patient city. PHI.',
      state: 'Patient state. PHI.',
      country: 'Patient country. PHI.',
      post_code: 'Patient postal code. PHI.',
      patient_identifier: 'Additional patient identifier (external system). PHI.',
      member_id: 'Insurance member ID. PHI.',
      contact_relation_name: 'Emergency / family contact name. PHI.',
      contact_relationship: 'Relationship of emergency contact to patient.',
      contact_relation_phone: 'Emergency contact phone. PHI.',
      gender: 'Duplicate of patient_gender (legacy column).',
      group_number: 'Insurance group number.',
      home_phone: 'Patient home phone. PHI.',
      policy_number: 'Insurance policy number.',
    },
  },

  rpt_all_referrals: {
    displayName: 'Referrals (flat view with users + facilities)',
    description:
      'Denormalized reporting view of referrals with provider + facility info attached. One row per referral.\n\n' +
      'NOTE — the field names below describe the cache shape AFTER our backend normalizes ' +
      'VitalEngine\'s response. VitalEngine\'s own API uses the prefixes created_* / sending_* / ' +
      'receiving_*, but empirically the values delivered under created_* are actually the SENDER ' +
      '(the referring physician — VE\'s "Referred By") and the values delivered under sending_* ' +
      'are actually the TYPIST (VE\'s "Created By"). DataCacheService.NormalizeReferralFieldNames ' +
      'rewrites every cached row at ingestion so the names finally match the data:\n' +
      '  • sending_*   → the SENDER (referring physician + their facility — the patient source).\n' +
      '  • creating_*  → the TYPIST (whoever clicked Create Referral). Often the same person as ' +
      'the sender; for inbound external (fax/phone) referrals this is the customer\'s intake nurse.\n' +
      '  • receiving_* → the DESTINATION physician + facility (where the patient is being sent).\n\n' +
      'The cache also preserves the original VE API values under _api_created_* and _api_sending_* ' +
      'for forensic comparison. Those backup fields should not appear in regular UI; they exist ' +
      'so the mapping can be verified against VE\'s docs and reversed without re-fetching if it ' +
      'ever turns out wrong. See REFERRAL-FIELD-NAMING.md for the full background.',
    columns: {
      referral_id: 'FK to rpt_referral.referral_id.',
      created_date: 'Referral creation timestamp. (NOT a role field — this is when the referral was created in VE.)',
      patient_first_name: 'Patient first name. PHI.',
      patient_last_name: 'Patient last name. PHI.',
      patient_dob: 'Patient date of birth. PHI.',
      referral_for: 'Reason for referral.',
      referral_type: 'Referral type enum.',
      referral_number: 'Human-readable referral number.',
      sending_user_id: 'User ID of the SENDER — the referring physician (the patient source).',
      sending_user_name: 'Denormalized name of the sender (referring physician).',
      sending_npi_number: 'NPI of the sender (referring physician), if applicable.',
      sending_speciality: 'Speciality text of the sender.',
      sending_facility_id: 'Facility ID of the sender — the patient\'s source facility.',
      sending_facility: 'Denormalized facility name of the sender.',
      creating_user_id: 'User ID of the TYPIST — whoever clicked Create Referral in the UI. Often the same person as sending_user_id; can also be an assistant or the destination\'s intake nurse for fax/phone referrals.',
      creating_user_name: 'Denormalized name of the typist.',
      creating_npi_number: 'NPI of the typist.',
      creating_speciality: 'Typist speciality text.',
      creating_facility_id: 'Typist\'s facility ID. For inbound external (fax/phone) referrals processed by intake staff this is the CUSTOMER\'S facility, not the sender\'s.',
      creating_facility: 'Denormalized typist facility name.',
      receiving_user_id: 'User ID of the DESTINATION physician.',
      receiving_user_name: 'Denormalized destination physician name.',
      receiving_npi_number: 'Destination NPI.',
      receiving_speciality: 'Destination speciality text.',
      receiving_facility_id: 'Destination facility ID. Most reports filter on this column.',
      receiving_facility: 'Denormalized destination facility name.',
      test_referral: '1 if the referral is flagged as a test / demo record and should be excluded from reports.',
      is_referred_by_non_vital_user: '1 if the sender (referred_by / sending side) is a non-VE placeholder physician in rpt_non_vital_physicians. Sourced from rpt_referral; present in the referrals API once VE exposes it on this view.',
      is_referred_to_non_vital_user: '1 if the destination (referred_to / receiving side) is a non-VE placeholder physician. Sourced from rpt_referral.',
      non_ve_physician_id: 'When the destination is non-VE, FK into rpt_non_vital_physicians.user_id. Sourced from rpt_referral.',
      _api_created_user_id: 'Forensic backup: original value VitalEngine\'s API delivered under "created_user_id". Empirically this is the SENDER (mirrored as sending_user_id). Don\'t use in regular UI; kept for verification.',
      _api_sending_user_id: 'Forensic backup: original value VitalEngine\'s API delivered under "sending_user_id". Empirically this is the TYPIST (mirrored as creating_user_id). Don\'t use in regular UI; kept for verification.',
    },
  },

  rpt_vendor_referrals: {
    displayName: 'Vendor referrals',
    description:
      'Same shape as rpt_all_referrals, but scoped to referrals involving a vendor (non-VE) facility. Produced by the nightly vendor reporting job.',
    columns: {},
  },

  rpt_referral_events: {
    displayName: 'Referral timeline events',
    description:
      'Timeline / audit log for a referral. Each row represents a state change, an assignment, or a system-generated event visible in the referral timeline UI.',
    columns: {
      event_id: 'Primary key.',
      referral_id: 'FK to rpt_referral.referral_id.',
      event_name: 'Event name / type (e.g. "Referral Created", "Accepted", "Declined", "Image Added").',
      event_date: 'Date portion of the event.',
      event_time: 'Time portion of the event.',
      telemedicine_appointment_id: 'If the event is an appointment scheduling, FK to rpt_telemedicine_appointments.',
    },
  },

  rpt_referral_messages: {
    displayName: 'Referral messages',
    description:
      'Chat-like messages posted inside a referral thread. This is where the back-and-forth between providers on a referral is captured.',
    columns: {
      referral_msg_id: 'Primary key.',
      referral_id: 'FK to rpt_referral.referral_id.',
      message: 'Message body. Potentially contains PHI typed by users.',
      count: 'Annotation counter (e.g. reactions / pings).',
    },
  },

  rpt_referral_participants: {
    displayName: 'Referral participants',
    description:
      'Join table between referrals and users. Controls who sees the referral in their inbox, read / unread state, and per-participant flags (pin, folder, order).',
    columns: {
      participant_id: 'Primary key.',
      referral_id: 'FK to rpt_referral.referral_id.',
      user_id: 'Participant user ID.',
      added_by: 'User ID that added this participant.',
      participant_type: 'Role of the participant in the referral (sender / receiver / cc / observer).',
      is_non_vital_user: '1 if user_id actually refers to a non-VE physician (see rpt_non_vital_physicians).',
    },
  },

  rpt_referral_tags: {
    displayName: 'Referral tags',
    description:
      'Tags applied to a referral. Tags are user-defined labels used for filtering and reporting.',
    columns: {
      referral_id: 'FK to rpt_referral.referral_id.',
      tag_id: 'FK to the tags master (not replicated here).',
      tag_name: 'Denormalized tag label.',
    },
  },

  rpt_referral_images: {
    displayName: 'Referral images / DICOM attachments',
    description:
      'DICOM and image attachments on a referral. Each row describes one study / image pointer stored in S3.',
    columns: {
      referral_id: 'FK to rpt_referral.referral_id.',
      name: 'Display name of the attachment.',
      study_instance_uid: 'DICOM Study Instance UID (joins with rpt_study).',
      http_path: 'S3 path to the zip / DICOM payload.',
      modality: 'DICOM modality (CT, MR, XR, etc.).',
      study_date: 'DICOM study date.',
      dicom_img_status: 'Processing status code for the DICOM pipeline.',
    },
  },

  rpt_referral_whiteboard: {
    displayName: 'Referral ↔ whiteboard links',
    description:
      'Pins a referral to a facility whiteboard. A referral can be on multiple whiteboards; deleted rows mean the referral was removed from that whiteboard.',
    columns: {
      referral_whiteboard_id: 'Primary key.',
      referral_id: 'FK to rpt_referral.referral_id.',
      whiteboard_id: 'FK to rpt_whiteboard.whiteboard_id.',
      include_on_whiteboard: '1 if the referral should currently show on the whiteboard.',
    },
  },

  rpt_referral_with_groups: {
    displayName: 'Referrals routed to groups',
    description:
      'Referrals that were sent to a group / queue rather than (or in addition to) a single user. Used for "hospital transfer" and referral-wizard flows where a group owns the inbox. Cached locally as DataType "group-referrals" (VitalEngine V2 endpoint /api/v2/{hospital}/group-referrals). PHI denylist applied server-side: patient_name and patient_date are stripped.',
    columns: {
      referral_id: 'FK to rpt_referral.referral_id.',
      referral_number: 'Denormalized human-readable number.',
      patient_name: 'Denormalized patient name. PHI.',
      patient_date: 'Denormalized patient DOB. PHI.',
      referral_for: 'Reason for referral.',
      referral_status: 'Status at time of snapshot.',
      receiving_group_id: 'FK to rpt_user_groups.group_id of the group that owns the queue.',
      receiving_group_name: 'Denormalized group name.',
      is_private_group: '1 if the group is private (restricted membership).',
      is_referral_wizard: '1 if the referral came through the referral-wizard flow.',
      referral_msg_id: 'FK to the first message on the referral.',
      referral_first_msg_created: 'Timestamp of the first message (useful for "time to first response" metrics).',
    },
  },

  // ---------- MESSAGING ----------
  rpt_chats: {
    displayName: 'Chats',
    description:
      'A chat thread: either a 1-on-1 DM or a group chat. rpt_chat_participants lists the members; rpt_chat_message lists the messages.',
    columns: {
      chat_id: 'Primary key.',
      group_name: 'Label shown in the chat list. Populated for group chats; typically null for DMs.',
      is_group: '1 if this is a group chat.',
      group_type: 'Group chat sub-type (e.g. clinical team, ad-hoc).',
      ongoing_call_id: "Call ID of the in-progress video / audio call, if any.",
      related_to_patient_id: 'If the chat was opened in the context of a specific patient, the patient ID.',
    },
  },

  rpt_chat_message: {
    displayName: 'Chat messages',
    description:
      'Individual messages inside a chat. Supports text, inline DICOM references, replies, forwards, labels, and soft-delete.',
    columns: {
      chat_msg_id: 'Primary key.',
      chat_id: 'FK to rpt_chats.chat_id.',
      chat_message: 'Message body (text / rich text).',
      study_instance_uid: 'If the message contains an attached DICOM study, the study UID.',
      modality: 'DICOM modality (if attached).',
      study_date: 'DICOM study date (if attached).',
      reply_msg_id: 'FK to the chat_msg_id this message is a reply to.',
      forward_chat_msg_id: 'FK to the chat_msg_id this message was forwarded from.',
      room_name: 'Video / audio room name, if the message was an in-call artifact.',
      dicom_img_status: 'Processing status of the DICOM attachment.',
      is_high_priority: 'Author marked the message high-priority.',
      requires_response: "Message requires explicit acknowledgment from recipients.",
      is_informational_only: 'Message is informational and does not require a response.',
      is_fragment: '1 if this is a fragment of a larger message (long text splits).',
      label: 'User-assigned label for the message.',
    },
  },

  rpt_chat_participants: {
    displayName: 'Chat participants',
    description:
      'Membership of a chat — one row per (chat, user). Also stores the per-user unread state.',
    columns: {
      participant_id: 'Primary key.',
      chat_id: 'FK to rpt_chats.chat_id.',
      user_id: 'Participant user ID.',
      added_by: 'User ID that added this participant.',
    },
  },

  rpt_vmails: {
    displayName: 'VMails (threads)',
    description:
      'Asynchronous clinical message threads. Carries subject, most recent message, and routing. Participants and messages live in the sibling tables.',
    columns: {
      vmail_id: 'Primary key.',
      subject: 'Thread subject line.',
      is_message: '1 if the thread was created as a message (vs. a records-transfer).',
      referred_to: 'User ID or group ID the vmail is addressed to.',
    },
  },

  rpt_vmail_messages: {
    displayName: 'VMail messages',
    description:
      'Individual messages inside a VMail thread.',
    columns: {
      conv_msg_id: 'Primary key.',
      conversation_id: 'FK to rpt_vmails.vmail_id.',
      message: 'Message body. May contain PHI typed by users.',
    },
  },

  rpt_vmail_participants: {
    displayName: 'VMail participants',
    description:
      'Per-user state for a VMail thread: folder placement, unread count, pin, ordering.',
    columns: {
      participant_id: 'Primary key.',
      vmail_id: 'FK to rpt_vmails.vmail_id.',
      user_id: 'Participant user ID.',
      participant_type: 'Role in the thread (to / cc / bcc).',
    },
  },

  rpt_vmail_images: {
    displayName: 'VMail DICOM images',
    description:
      'DICOM studies attached to a VMail.',
    columns: {
      vmail_id: 'FK to rpt_vmails.vmail_id.',
      name: 'Attachment display name.',
      study_instance_uid: 'DICOM Study Instance UID.',
      http_path: 'S3 path to the zipped DICOM payload.',
      modality: 'DICOM modality.',
      study_date: 'DICOM study date.',
      patient_name: 'Patient name extracted from DICOM. PHI.',
      dicom_img_status: 'Processing status of the DICOM attachment.',
    },
  },

  rpt_vmail_medical_records: {
    displayName: 'VMail documents',
    description:
      'Non-DICOM document attachments on a VMail (PDFs, images, etc.).',
    columns: {
      vmail_id: 'FK to rpt_vmails.vmail_id.',
      name: 'Attachment file name.',
      http_path: 'S3 path to the file.',
      category_id: 'Category / classification of the document.',
    },
  },

  // ---------- VROUNDS ----------
  rpt_vrounds: {
    displayName: 'VRounds sessions',
    description:
      'A scheduled multi-disciplinary case review session. Contains cases (rpt_vrounds_cases) and participants.',
    columns: {
      vround_id: 'Primary key.',
      vround_name: 'Display name of the round.',
      video_meeting_id: 'FK to the video meeting that hosts this round, if any.',
      vround_date: 'Scheduled date / time of the round.',
    },
  },

  rpt_vrounds_cases: {
    displayName: 'VRounds cases',
    description:
      'A single case inside a VRound. Can reference a referral for patient context, and carries case-specific notes and conclusions.',
    columns: {
      vround_case_id: 'Primary key.',
      vround_id: 'FK to rpt_vrounds.vround_id.',
      referral_id: 'Optional FK to rpt_referral.referral_id when the case originated from a referral.',
      case_name: 'Short case title.',
      patient_status: 'Current patient status (free text).',
      patient_summary: 'One-paragraph summary typed by the presenter. PHI.',
      patient_relevant_history: 'Relevant history typed by the presenter. PHI.',
      specialist_type: 'Type of specialist the case was presented to.',
      specialist_name: 'Name of the presenting specialist.',
      case_conclusions: 'Conclusions / plan captured during discussion. PHI.',
      case_date: 'When the case was discussed.',
    },
  },

  rpt_vrounds_cases_referrals: {
    displayName: 'VRounds case ↔ referral links',
    description:
      'Join table: one case can be associated with multiple referrals (complex multi-referral patients).',
    columns: {
      vround_case_id: 'FK to rpt_vrounds_cases.vround_case_id.',
      referral_id: 'FK to rpt_referral.referral_id.',
    },
  },

  rpt_vrounds_participants: {
    displayName: 'VRounds participants',
    description:
      'Membership and admin state for a VRound session.',
    columns: {
      vround_id: 'FK to rpt_vrounds.vround_id.',
      user_id: 'Participant user ID.',
      is_admin_rights: '1 if the participant can edit the round (add cases, reorder, etc.).',
      removed_by: 'Actor who removed the participant (legacy column; stored as datetime).',
      removed_date: 'When the participant was removed.',
    },
  },

  // ---------- STUDIES ----------
  rpt_study: {
    displayName: 'Studies',
    description:
      'A DICOM study received by VitalEngine. One row per Study Instance UID per facility. Cached locally as DataType "study-details" (VitalEngine V2 endpoint /api/v2/{hospital}/study-details) — distinct from "studies" which is the daily aggregate view. PHI denylist applied server-side: study_instance_uid, dicom_study_id, assession_number, and referring_physician_name are stripped. Soft-deleted rows (delete_at IS NOT NULL) are also excluded server-side.',
    columns: {
      study_id: 'Primary key.',
      study_instance_uid: 'DICOM Study Instance UID (authoritative).',
      dicom_study_id: 'DICOM study ID tag.',
      referring_physician_name: 'Referring physician name from DICOM metadata. PHI.',
      study_description: 'DICOM study description.',
      facility_id: 'FK to rpt_facility.facility_id that received the study.',
      modalities: 'Comma-separated modalities present in the study (e.g. "CT, CR").',
      user_id: 'User who received / imported the study.',
      study_date_time: 'Acquisition date/time from DICOM.',
      assession_number: 'Accession number from DICOM.',
      process_type: 'Pipeline that processed the study (upload / vmail / referral / etc.).',
      delete_at: 'Soft-delete timestamp.',
    },
  },

  rpt_study_images: {
    displayName: 'Study images / processing metadata',
    description:
      'Per-image processing records for studies (sender, recipient, routing, status). Produced by the inbound DICOM pipeline.',
    columns: {
      study_image_id: 'Primary key.',
      study_id: 'FK to rpt_study.study_id.',
      meta_data_study_id: 'FK to rpt_meta_data_study.',
      study_instance_uid: 'Study UID copy for convenience.',
      processing_status: 'Pipeline processing status code.',
      zip_file_path: 'S3 path to the zipped DICOM.',
      referral_id: 'FK to rpt_referral.referral_id if routed through a referral.',
      facility_id: 'Originating facility.',
      receiver_facility_id: 'Destination facility.',
      study_series_image_count: 'Comma-separated triplet of series / image counts.',
    },
  },

  rpt_study_permissions: {
    displayName: 'Study access permissions',
    description:
      'Per-user or per-facility access grants to a study. Records who has been given visibility beyond the default.',
    columns: {
      study_id: 'FK to rpt_study.study_id.',
      study_instance_uid: 'Study UID copy.',
      facility_id: 'Facility granted access.',
      user_id: 'User granted access.',
      permission_by: 'Source of the permission (manual / inherited / etc.).',
      create_at: 'UTC timestamp when the permission was granted.',
    },
  },

  rpt_dicom_outgoing_file: {
    displayName: 'Outbound DICOM transfers',
    description:
      'Files queued for transfer out of VitalEngine to an external DICOM device (PACS / DICOM listener).',
    columns: {
      device_id: 'Target DICOM device ID.',
      study_instance_uid: 'Study being transferred.',
      zip_file_path: 'Local / S3 path to the payload.',
      sent_by: 'Transfer mode enum (manual / auto-forward / etc.).',
      transferred_files_count: 'Number of files successfully transferred so far.',
      total_files: 'Total number of files in the payload.',
      dicom_association_id: 'DIMSE association ID for the transfer.',
      patient_name: 'DICOM patient name. PHI.',
      patient_birth_date: 'DICOM patient DOB. PHI.',
      exception: 'Exception message, if the transfer failed.',
    },
  },

  rpt_study_summarized_usage: {
    displayName: 'Study usage summary (daily per facility)',
    description:
      'Daily rollup of studies sent and received per facility and processing pipeline. Used for usage dashboards.',
    columns: {
      created_date: 'UTC date of the usage bucket.',
      facility_id: 'Facility bucketed on.',
      process_type: 'Pipeline used (upload / vmail / referral / etc.).',
      facility_name: 'Denormalized facility name.',
      facility_physical_city: 'Facility physical city.',
      facility_physical_state: 'Facility physical state.',
      practice_type: 'Facility practice type (hospital, clinic, imaging, etc.).',
      study_sent: 'Count of studies sent that day.',
      study_received: 'Count of studies received that day.',
    },
  },

  // ---------- TELEMEDICINE ----------
  rpt_telemedicine_appointments: {
    displayName: 'Telemedicine appointments',
    description:
      'Scheduled telemedicine visits.',
    columns: {
      telemedicine_appointment_id: 'Primary key.',
      name: 'Appointment title / reason.',
      appointment_start_utc: 'Scheduled start (UTC).',
      appointment_end_utc: 'Scheduled end (UTC).',
      facility_id: 'Host facility.',
      minutes: 'Planned duration.',
      notify_participants: 'Send reminders to participants.',
      notes: 'Appointment notes. PHI.',
      is_completed: 'Appointment was marked completed.',
      meeting_provider_id: 'Video meeting provider (Zoom, native, etc.) ID.',
    },
  },

  rpt_telemedicine_appointment_participants: {
    displayName: 'Telemedicine appointment participants',
    description:
      'Attendees for a telemedicine visit: providers, patients, and guest (non-user) invitees.',
    columns: {
      telemedicine_appointment_participant_id: 'Primary key.',
      telemedicine_appointment_id: 'FK to rpt_telemedicine_appointments.',
      user_id: 'VitalEngine user attendee.',
      physician_id: 'Physician attendee (if distinct).',
      patient_id: 'Patient attendee.',
      added_date: 'UTC timestamp when the attendee was added.',
      is_joined: '1 if the attendee joined the call.',
      participant_join_code: 'Per-attendee join code used for guest links.',
      is_guest: '1 if the attendee is a non-user guest.',
      guest_first_name: 'Guest first name. PHI for patients.',
      guest_last_name: 'Guest last name. PHI for patients.',
      guest_phone_number: 'Guest phone. PHI.',
      guest_email_address: 'Guest email. PHI.',
    },
  },

  // ---------- USERS & FACILITIES ----------
  rpt_user: {
    displayName: 'Users',
    description:
      'The VitalEngine user directory. One row per user (clinical or admin).',
    columns: {
      user_id: 'Primary key.',
      facility_id: 'Primary facility.',
      physician_id: 'FK to the physician record (for users who are physicians).',
      user_name: 'Login name.',
      first_name: 'First name.',
      last_name: 'Last name.',
      display_name: 'Display name shown in the UI.',
      speciality: 'Speciality text.',
      department: 'Department text.',
      user_type: 'Role / type code (provider, admin, staff, etc.).',
      npi_number: 'NPI, if applicable.',
      primary_email: 'Primary email address.',
      cc_email_address: 'Secondary (cc) email.',
      dob: 'Date of birth.',
      status: 'Account status (active / disabled / pending).',
      last_login: 'Most recent successful login.',
      primary_mobile: 'Primary mobile number.',
      desktop_last_login: 'Most recent desktop client login.',
      mobile_last_login: 'Most recent mobile client login.',
      is_enable_2FA: '1 if 2FA is enabled on the account.',
      mobile_verified: '1 if the mobile number has been verified.',
    },
  },

  rpt_prod_users: {
    displayName: 'Production user auth records',
    description:
      'Authentication-focused subset of the user directory, pulled from the production auth service. Contains hashed passwords and lockout state.',
    columns: {
      user_id: 'FK to rpt_user.user_id.',
      user_name: 'Login name.',
      password: 'Salted hash of the password (never the plaintext).',
      user_type: 'Role / type code.',
      status: 'Account status.',
      last_login: 'Most recent successful login.',
      auth_code: 'One-time auth code (e.g. password reset).',
      email: 'Primary email.',
      system_generated_password: '1 if the current password was system-generated.',
      pwd_last_changed: 'When the password was last changed.',
      loginas_user_token: '"Login as" impersonation token (admin).',
      is_login: '1 if currently considered logged in.',
      first_time_login: '1 until the user completes the first-login flow.',
      pswd_attempt_fail_count: 'Consecutive failed-login count.',
      pswd_attempt_fail_date: 'Timestamp of the most recent failed login.',
      is_pswd_expired: '1 if the password has expired.',
      hipaa_agreement_id: 'ID of the HIPAA agreement version the user accepted.',
      primary_mobile: 'Mobile number for 2FA.',
      electron_first_time_login: 'First-login state for the Electron desktop app.',
      is_desktop_login: '1 if logged in via desktop.',
      is_mobile_login: '1 if logged in via mobile.',
      desktop_last_login: 'Desktop last-login timestamp.',
      mobile_last_login: 'Mobile last-login timestamp.',
      is_enable_2FA: '1 if 2FA is enabled.',
      mobile_verified: '1 if mobile has been verified.',
      verification_code: 'Active verification code (SMS / email).',
      generated_date: 'When the verification code was generated.',
      super_admin: '1 if this is a super-admin account.',
      mobile_first_time_login: 'First-login state on mobile.',
      is_enable_react_ui: '1 if the user is on the new React UI.',
      hipaa_agreed_at: 'When the user accepted the current HIPAA agreement.',
      forget_password_at: 'Most recent forgot-password request.',
      is_primary_email_verified: '1 if the primary email has been verified.',
      primary_email_last_verified_at: 'Last successful primary-email verification.',
      admin_user: '1 if this is an admin account (facility / organization admin).',
    },
  },

  rpt_user_facilities: {
    displayName: 'User ↔ facility links',
    description:
      'Which facilities a user belongs to. A user can belong to multiple facilities; is_primary marks the default.',
    columns: {
      user_id: 'FK to rpt_user.user_id.',
      facility_id: 'FK to rpt_facility.facility_id.',
      is_primary: '1 if this is the user\'s primary facility.',
      saml_user_name: 'SAML principal mapping for this user within this facility.',
    },
  },

  rpt_user_groups: {
    displayName: 'User groups / queues',
    description:
      'Group / queue definitions. Groups are shared inboxes: members of a group all see messages / referrals / faxes sent to the group.',
    columns: {
      group_id: 'Primary key.',
      group_name: 'Group display name.',
      user_id: 'Owner user ID.',
      facility_id: 'Facility the group belongs to.',
      is_private_group: '1 if membership is restricted.',
      fax_number: 'Associated fax number for inbound faxes.',
      is_hospital_transfer: '1 if this group handles hospital transfer referrals.',
      is_referral_wizard: '1 if this group receives referrals from the referral-wizard flow.',
      referral_phone_number: 'Phone number shown on referrals routed to this group.',
    },
  },

  rpt_user_group_members: {
    displayName: 'User group members',
    description:
      'Membership of a user group. Mix of clinical and admin members depending on the group purpose.',
    columns: {
      group_id: 'FK to rpt_user_groups.group_id.',
      user_id: 'Member user ID.',
      unread_fax_count: 'Unread inbound-fax count for this member.',
    },
  },

  rpt_user_login_history: {
    displayName: 'User login history (daily)',
    description:
      'Daily rollup of successful logins per user per client type. This is the source of the "logins" metric.',
    columns: {
      user_id: 'FK to rpt_user.user_id.',
      created_date: 'UTC date of the login bucket.',
      client_type: 'Client type (web / desktop / mobile / telemedicine).',
      daily_login_count: 'Number of successful logins for that user, day, and client.',
    },
  },

  rpt_user_phone: {
    displayName: 'User phone numbers',
    description:
      'All phone numbers a user has registered, with type (mobile / office / home / fax).',
    columns: {
      phone_id: 'Primary key.',
      user_id: 'FK to rpt_user.user_id.',
      phone_type: 'mobile / office / home / fax.',
      phone_number: 'The phone number itself.',
    },
  },

  rpt_non_vital_physicians: {
    displayName: 'Non-VitalEngine physicians',
    description:
      'Physicians not on VitalEngine who are referenced as referral targets (so a VE user can refer out to them). Managed by the referring facility.',
    columns: {
      user_id: 'Primary key (distinct ID space from rpt_user).',
      first_name: 'First name.',
      middle_name: 'Middle name.',
      last_name: 'Last name.',
      title: 'Title / prefix.',
      suffix: 'Suffix (MD, DO, NP, etc.).',
      speciality: 'Speciality text.',
      npi_number: 'NPI.',
      npi_deactivation_date: 'NPI deactivation date, if the provider was removed.',
      primary_email: 'Primary email.',
      status: 'Active / inactive flag.',
    },
  },

  rpt_physician_assistants: {
    displayName: 'Physician ↔ assistant links',
    description:
      'Assistants who act on behalf of a physician (e.g. MAs, coordinators). They can see / act on the physician\'s referrals.',
    columns: {
      id: 'Primary key.',
      assistant_id: 'FK to rpt_user.user_id (the assistant).',
      physician_id: 'FK to rpt_user.user_id (the physician).',
    },
  },

  rpt_facility: {
    displayName: 'Facilities',
    description:
      'The master facility directory. Used by reports, hierarchy visualization, and facility selection.',
    columns: {
      facility_id: 'Primary key.',
      user_id: 'Owner user for the facility (typically the admin who created it).',
      facility_name: 'Facility display name.',
      user_name: 'Denormalized owner user name.',
      mailing_address1: 'Mailing address line 1.',
      mailing_address2: 'Mailing address line 2.',
      mailing_city: 'Mailing city.',
      mailing_state: 'Mailing state.',
      mailing_postal_code: 'Mailing ZIP / postal code.',
      mailing_country: 'Mailing country.',
      physical_address1: 'Physical address line 1.',
      physical_address2: 'Physical address line 2.',
      physical_city: 'Physical city.',
      physical_state: 'Physical state.',
      physical_postal_code: 'Physical ZIP / postal code.',
      physical_country: 'Physical country.',
      practice_type: 'Practice type (hospital, clinic, imaging, etc.).',
      primary_email: 'Primary facility contact email.',
    },
  },

  // ---------- USAGE SUMMARIES ----------
  rpt_VE_usage: {
    displayName: 'Daily VE usage counts',
    description:
      'Tiny rollup of per-user-per-day usage by a single usage_type string. The most granular "did this user do anything today" signal.',
    columns: {
      created_date: 'UTC date of the bucket.',
      usage_type: 'Usage bucket name (login / message / referral / study / etc.).',
      user_id: 'User the count is attributed to.',
      count: 'Count of events for that user/day/type.',
    },
  },

  rpt_consolidated_group_usage: {
    displayName: 'Group messaging usage summary',
    description:
      'Daily group-messaging rollup: for each (date, chat/group) the count of messages sent vs. received.',
    columns: {
      created_date: 'UTC date bucket.',
      chat_id: 'FK to rpt_chats.chat_id.',
      group_name: 'Denormalized group name.',
      facility_id: 'Facility bucketed on.',
      facility_name: 'Denormalized facility name.',
      facility_physical_city: 'Facility city.',
      facility_physical_state: 'Facility state.',
      practice_type: 'Facility practice type.',
      group_messages_received: 'Messages received by the group that day.',
      group_messages_sent: 'Messages sent by the group that day.',
    },
  },

  rpt_consolidated_message_usage: {
    displayName: 'Messaging usage summary (per user)',
    description:
      'Daily per-user messaging rollup across all VE message types. Powers the "Messages" metric on reports.',
    columns: {
      created_date: 'UTC date bucket.',
      user_id: 'User the counts are attributed to.',
      user_display_name: 'Denormalized display name.',
      speciality: 'Denormalized speciality.',
      user_type: 'Denormalized user type / role.',
      user_physical_city: 'User city.',
      user_physical_state: 'User state.',
      facility_id: 'User primary facility.',
      facility_name: 'Denormalized facility name.',
      facility_physical_city: 'Facility city.',
      facility_physical_state: 'Facility state.',
      practice_type: 'Facility practice type.',
      referral_messages_sent: 'Messages sent on referrals.',
      vmail_messages_Sent: 'VMail messages sent.',
      vchat_messages_Sent: 'Chat messages sent.',
      vchat_messages_received: 'Chat messages received.',
      vround_messages_sent: 'Messages sent inside VRound cases.',
      vtransfer_messages_sent: 'Messages sent via hospital-transfer workflow.',
      Other_messages_sent: 'Fallback bucket for messages that don\'t fit another type.',
    },
  },

  rpt_user_summarized_login_usage: {
    displayName: 'Login usage summary',
    description:
      'Daily per-user logins broken out by client type. Built from rpt_user_login_history.',
    columns: {
      created_date: 'UTC date bucket.',
      user_id: 'User ID.',
      user_display_name: 'Denormalized display name.',
      speciality: 'User speciality.',
      user_type: 'User type / role.',
      facility_id: 'Primary facility.',
      facility_name: 'Denormalized facility name.',
      web_login: 'Web client logins that day.',
      goweb_login: 'Go-Web client logins.',
      common_login: 'Logins via the shared common client.',
      desktop_login: 'Electron / desktop logins.',
      mobile: 'Mobile app logins.',
      telemedicine_login: 'Telemedicine-link logins.',
    },
  },

  rpt_temp_group_message_usage: {
    displayName: 'Group message usage (staging)',
    description:
      'Nightly staging input that feeds rpt_consolidated_group_usage. Considered internal — prefer the consolidated table for reporting.',
    columns: {},
  },

  rpt_temp_user_message_usage: {
    displayName: 'User message usage (staging)',
    description:
      'Nightly staging input that feeds rpt_consolidated_message_usage. Prefer the consolidated table for reporting.',
    columns: {},
  },

  rpt_temp_study_summarized_facility_usage: {
    displayName: 'Study usage (staging)',
    description:
      'Nightly staging input that feeds rpt_study_summarized_usage.',
    columns: {},
  },

  // ---------- WHITEBOARDS & TASKS ----------
  rpt_whiteboard: {
    displayName: 'Whiteboards',
    description:
      'Shared facility worklists. Whiteboards are owned by a facility and can pin referrals for visibility by a team.',
    columns: {
      whiteboard_id: 'Primary key.',
      whiteboard_name: 'Display name.',
      facility_id: 'Owning facility.',
      is_show_to_group: '1 if the whiteboard is shared to a specific user group.',
    },
  },

  rpt_whiteboard_workflow_order: {
    displayName: 'Whiteboard workflow ordering',
    description:
      'Sort-order configuration for whiteboards (which column to sort by and in what direction).',
    columns: {
      whiteboard_workflow_order_id: 'Primary key.',
      whiteboard_id: 'FK to rpt_whiteboard.whiteboard_id.',
      sort_by_order: 'asc / desc.',
      sort_by_procedure_id: 'Procedure / column key to sort on.',
    },
  },

  rpt_vtask: {
    displayName: 'VTasks',
    description:
      'User-scoped tasks / reminders. Can optionally link to a referral for context.',
    columns: {
      task_id: 'Primary key.',
      title: 'Task title.',
      note: 'Free-text body.',
      due_date: 'Scheduled due date.',
      list_id: 'Task list the task belongs to.',
      referral_id: 'Optional FK to rpt_referral.referral_id.',
      is_stared: 'Starred / flagged flag.',
      is_delteted: 'Soft-delete flag (sic — original column name is misspelled).',
      is_completed: 'Completed flag.',
    },
  },

  rpt_vtask_users: {
    displayName: 'VTask user assignments',
    description:
      'Which users are assigned to / notified about a task. A task can be shared with many users.',
    columns: {
      task_id: 'FK to rpt_vtask.task_id.',
      user_id: 'Assigned user.',
      reminder_date: 'When to remind this user about the task.',
      is_owner: '1 if this user owns the task.',
      is_assigned: '1 if the user is assigned.',
      is_shared: '1 if the task is shared read-only to this user.',
    },
  },

  // ---------- INBOUND ----------
  rpt_fax_data: {
    displayName: 'Faxes',
    description:
      'Inbound and outbound fax records. Metadata only; the PDF lives in S3 under rpt_meta_data. Cached locally as DataType "faxes" (VitalEngine V2 endpoint /api/v2/{hospital}/faxes). PHI denylist applied server-side: fax_from and fax_to (phone numbers) are stripped.',
    columns: {
      fax_id: 'Primary key.',
      num_pages: 'Page count (stored as text).',
      fax_status: 'Status (received / sent / failed / etc.).',
      fax_from: 'Sender fax number.',
      fax_to: 'Recipient fax number.',
      facility_id: 'Facility that the fax routed to.',
      group_id: 'User group that the fax was delivered to.',
    },
  },

  rpt_fax_data_recipients: {
    displayName: 'Fax recipients',
    description:
      'Per-user read state for a fax. Each user in the receiving group has a row until they read it. Cached locally as DataType "fax-recipients" (VitalEngine V2 endpoint /api/v2/{hospital}/fax-recipients). Available to VERA for ad-hoc queries (e.g. "what % of faxes at facility X go unread"); not surfaced as a primary tile in Data Trends / Insights / Raw Data.',
    columns: {
      fax_id: 'FK to rpt_fax_data.fax_id.',
      user_id: 'Recipient user.',
      is_read: '1 if the user has opened the fax.',
    },
  },

  rpt_meta_data: {
    displayName: 'Inbound metadata',
    description:
      'Metadata for all inbound content streams (email → study, fax → study, vendor imports, etc.). Drives the inbound-processing pipeline.',
    columns: {
      id: 'Primary key.',
      sender_first_name: 'Sender first name. PHI.',
      sender_middle_name: 'Sender middle name. PHI.',
      sender_last_name: 'Sender last name. PHI.',
      sender_title: 'Sender title / prefix.',
      sender_practice_name: 'Sender practice / facility name.',
      sender_email: 'Sender email. PHI.',
      receiver_email: 'Receiver email. PHI.',
      patient_title: 'Patient title. PHI.',
      patient_first_name: 'Patient first name. PHI.',
      patient_last_name: 'Patient last name. PHI.',
      patient_middle_name: 'Patient middle name. PHI.',
      patient_dob: 'Patient DOB (stored as varchar to preserve source formatting). PHI.',
      message: 'Message body. Potentially PHI.',
      file_name: 'Attachment name.',
      processing_status: 'Pipeline status code.',
      retry_count: 'Retry attempts so far.',
      exception: 'Last exception message, if the pipeline failed.',
      subject: 'Email / message subject.',
      type: 'Inbound source type (email / fax / vendor / etc.).',
      zip_file_path: 'S3 path to the raw payload.',
      process_type: 'Internal processing variant.',
      sender_user_id: 'If the sender matches a VE user, their ID.',
      process_percentage: 'Progress indicator for long-running imports.',
      completed_date: 'When the pipeline finished this row.',
      referral_number: 'Resulting referral number, if the inbound produced one.',
      facility_id: 'Facility the inbound was routed to.',
      receiver_facility_id: 'Destination facility (may differ from facility_id for forwarded inbounds).',
      gu_first_name: 'Guest uploader first name (web submit form).',
      gu_middle_name: 'Guest uploader middle name.',
      gu_last_name: 'Guest uploader last name.',
      gu_email: 'Guest uploader email.',
      gu_contact_number: 'Guest uploader phone.',
      gu_facility_name: 'Guest uploader facility.',
      study_series_image_count: 'Triplet of series/image counts.',
      study_instance_uid: 'If the inbound produced a study, the resulting UID.',
    },
  },

  rpt_meta_data_study: {
    displayName: 'Inbound metadata ↔ study',
    description:
      'Links a rpt_meta_data row to the study UID(s) it produced. One inbound can produce multiple studies.',
    columns: {
      meta_data_id: 'FK to rpt_meta_data.id.',
      study_instance_uid: 'Study Instance UID produced by the inbound.',
    },
  },

  // ---------- ADMIN ----------
  rpt_config: {
    displayName: 'Report execution config',
    description:
      'Control table for the nightly reporting jobs: last execution timestamps and start-of-current-execution markers.',
    columns: {
      rpt_name: 'Report / job name.',
      rpt_last_executed: 'Most recent successful completion.',
      rpt_start_execution: 'Start time of the currently running execution, if any.',
    },
  },

  rpt_vendor_facility_id: {
    displayName: 'Vendor facility ID mapping',
    description:
      'Mapping from an external vendor\'s facility abbreviation to the VE facility ID. Used when ingesting vendor referral / study feeds.',
    columns: {
      facility_id: 'FK to rpt_facility.facility_id.',
      facility_name: 'Denormalized facility name.',
      vendor_abbrievation: "Vendor's short code / abbreviation for the facility.",
    },
  },
};

// Heuristic fallback used when neither the per-table map nor GENERIC_COLUMNS
// knows about the column.
export function heuristicDescription(colName) {
  const n = colName.toLowerCase();
  if (n.endsWith('_id') && n !== 'id') {
    const base = n.slice(0, -3).replace(/_/g, ' ');
    return `Foreign key — identifies the associated ${base}.`;
  }
  if (n.startsWith('is_')) return 'Flag — 1 if true, 0 otherwise.';
  if (n.startsWith('has_')) return 'Flag — 1 if true, 0 otherwise.';
  if (n.endsWith('_date') || n.endsWith('_at')) return 'Timestamp.';
  if (n.endsWith('_name')) return `Display name — ${n.replace(/_/g, ' ')}.`;
  if (n.endsWith('_count')) return 'Counter.';
  if (n.endsWith('_by')) return 'User ID — who performed the associated action.';
  if (n.endsWith('_type')) return 'Type / category enum.';
  if (n.endsWith('_status')) return 'Status enum.';
  return '';
}

export function describeColumn(tableName, column) {
  const t = TABLE_DOCS[tableName];
  if (t?.columns?.[column.name]) return t.columns[column.name];
  if (GENERIC_COLUMNS[column.name]) return GENERIC_COLUMNS[column.name];
  return heuristicDescription(column.name);
}

export function describeTable(tableName) {
  return TABLE_DOCS[tableName] || null;
}
